import { useCallback, useEffect, useRef, useState } from "react";
import {
  DEFAULT_AGENT,
  deleteKnowledge,
  fetchAgent,
  fetchKnowledge,
  saveAgent,
  saveKnowledge,
  testAgent,
  type KnowledgeItem,
  type TestTurn,
  type WaAgent,
} from "@/services/waAgent";
import { btnGhost, btnPrimary, cardCls, inputCls } from "./ui";

const STARTER: { title: string; question: string; answer: string }[] = [
  { title: "Qué es Vívelo", question: "¿Qué es Vívelo?", answer: "Vívelo AI ayuda a creadores y emprendedores a organizar sus hábitos, su prospección y su seguimiento con un sistema sencillo. Todo se explica en un webinar gratuito y corto." },
  { title: "Costo del webinar", question: "¿Tiene costo el webinar?", answer: "No. El webinar es gratuito y sin compromiso." },
  { title: "Duración y cómo verlo", question: "¿Cuánto dura y cómo lo veo?", answer: "Lo ves cuando quieras desde el enlace que te enviamos; puedes pausarlo y retomarlo donde lo dejaste. (Edita aquí la duración real del video.)" },
  { title: "Hablar con una persona", question: "Quiero hablar con alguien del equipo", answer: "Con gusto: una persona del equipo te escribirá por este mismo chat." },
];

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-foreground-700">{label}</span>
      {hint ? <span className="block text-[11px] text-foreground-500">{hint}</span> : null}
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Tester({ notify }: { notify: (m: string) => void }) {
  const [turns, setTurns] = useState<TestTurn[]>([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [flag, setFlag] = useState<string | null>(null);
  const end = useRef<HTMLDivElement>(null);

  useEffect(() => {
    end.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [turns, busy]);

  const send = async () => {
    const t = text.trim();
    if (!t || busy) return;
    const next: TestTurn[] = [...turns, { role: "user", content: t }];
    setTurns(next);
    setText("");
    setBusy(true);
    setFlag(null);
    const r = await testAgent(next, "Laura");
    setBusy(false);
    if (r.error || r.ok === false) {
      notify(r.error === "ollama_not_configured" ? "Falta OLLAMA_API_KEY en Supabase" : "El agente no pudo responder");
      return;
    }
    if (r.handoff) setFlag(r.reason || "El agente pasaría esta conversación a una persona");
    if (r.reply) setTurns([...next, { role: "assistant", content: r.reply }]);
  };

  return (
    <div className={cardCls}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading font-bold text-foreground-950">Probador</h3>
        <button className="text-xs font-semibold text-foreground-500 hover:text-foreground-900 cursor-pointer" onClick={() => { setTurns([]); setFlag(null); }}>
          Reiniciar
        </button>
      </div>
      <p className="text-[11px] text-foreground-500 mb-3">Escribe como si fueras un cliente. Usa la última configuración <b>guardada</b> y no envía nada por WhatsApp.</p>
      <div className="h-72 overflow-y-auto rounded-xl bg-background-50 border border-background-200 p-3 space-y-2">
        {!turns.length ? <div className="text-xs text-foreground-400 text-center mt-24">Ej.: “Hola, ¿cuánto cuesta?” · “¿Es una estafa?” · “Quiero hablar con alguien”</div> : null}
        {turns.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-primary-500 text-background-950" : "bg-background-100 border border-background-200 text-foreground-900"}`}>{m.content}</div>
          </div>
        ))}
        {busy ? <div className="text-xs text-foreground-500">Escribiendo…</div> : null}
        <div ref={end}></div>
      </div>
      {flag ? (
        <div className="mt-2 text-xs rounded-lg bg-amber-500/10 text-amber-800 px-3 py-2">
          <i className="ri-user-voice-line mr-1"></i>Traspaso a humano: {flag}
        </div>
      ) : null}
      <div className="flex gap-2 mt-3">
        <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && void send()} placeholder="Mensaje del cliente…" className={inputCls} />
        <button className={btnPrimary} disabled={busy || !text.trim()} onClick={() => void send()}>
          Enviar
        </button>
      </div>
    </div>
  );
}

function KnowledgeEditor({ item, onDone, notify }: { item: Partial<KnowledgeItem> | null; onDone: () => void; notify: (m: string) => void }) {
  const [k, setK] = useState({ title: item?.title ?? "", question: item?.question ?? "", answer: item?.answer ?? "", active: item?.active ?? true });
  const [busy, setBusy] = useState(false);
  return (
    <div className="rounded-xl border border-primary-400/50 bg-background-50 p-4 space-y-3">
      <Field label="Tema (solo para ti)">
        <input value={k.title} onChange={(e) => setK({ ...k, title: e.target.value })} className={inputCls} maxLength={80} />
      </Field>
      <Field label="Pregunta típica del cliente">
        <input value={k.question} onChange={(e) => setK({ ...k, question: e.target.value })} className={inputCls} maxLength={200} />
      </Field>
      <Field label="Respuesta correcta" hint="Escríbela como quieres que el agente la diga. Sin promesas de ingresos.">
        <textarea rows={4} value={k.answer} onChange={(e) => setK({ ...k, answer: e.target.value })} className={`${inputCls} resize-y`} maxLength={1200} />
      </Field>
      <div className="flex items-center justify-between">
        <label className="text-xs text-foreground-700 flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={k.active} onChange={(e) => setK({ ...k, active: e.target.checked })} className="w-4 h-4 accent-primary-500" />
          Activa
        </label>
        <div className="flex gap-2">
          <button className={btnGhost} onClick={onDone}>
            Cancelar
          </button>
          <button
            className={btnPrimary}
            disabled={busy || !k.answer.trim() || !(k.title.trim() || k.question.trim())}
            onClick={async () => {
              setBusy(true);
              try {
                await saveKnowledge({ id: item?.id, title: k.title.trim() || k.question.trim().slice(0, 60), question: k.question, answer: k.answer, active: k.active });
                notify("Conocimiento guardado");
                onDone();
              } catch {
                notify("No se pudo guardar");
              } finally {
                setBusy(false);
              }
            }}
          >
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AgentTab({ notify }: { notify: (m: string) => void }) {
  const [a, setA] = useState<WaAgent>(DEFAULT_AGENT);
  const [saved, setSaved] = useState<WaAgent>(DEFAULT_AGENT);
  const [kb, setKb] = useState<KnowledgeItem[]>([]);
  const [editing, setEditing] = useState<Partial<KnowledgeItem> | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [ag, k] = await Promise.all([fetchAgent(), fetchKnowledge()]);
      setA(ag);
      setSaved(ag);
      setKb(k);
    } catch {
      notify("No se pudo cargar el agente");
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    void load();
  }, [load]);

  const dirty = JSON.stringify(a) !== JSON.stringify(saved);
  const set = <K extends keyof WaAgent>(key: K, v: WaAgent[K]) => setA((p) => ({ ...p, [key]: v }));

  const save = async (next: WaAgent = a) => {
    setBusy(true);
    try {
      await saveAgent(next);
      setSaved(next);
      setA(next);
      notify("Agente guardado");
    } catch {
      notify("No se pudo guardar");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="text-sm text-foreground-500">Cargando…</div>;

  return (
    <div className="grid lg:grid-cols-[minmax(0,1fr)_380px] gap-5 items-start">
      <div className="space-y-4 min-w-0">
        <div className={`${cardCls} flex flex-wrap items-center justify-between gap-3`}>
          <div>
            <div className="font-heading font-bold text-foreground-950">Responder automáticamente</div>
            <div className="text-xs text-foreground-500 max-w-md">
              {saved.enabled
                ? `${saved.agentName} responde solo, hasta ${saved.maxAutoReplies} mensajes seguidos por conversación, y pasa a una persona si no sabe, hay queja o la pide.`
                : "Apagado: los mensajes llegan a la Bandeja y los respondes tú."}
            </div>
          </div>
          <button
            role="switch"
            aria-checked={a.enabled}
            disabled={busy}
            onClick={() => void save({ ...a, enabled: !a.enabled })}
            className={`relative w-14 h-8 rounded-full transition-colors cursor-pointer ${a.enabled ? "bg-primary-500" : "bg-background-300"}`}
          >
            <span className={`absolute top-1 w-6 h-6 rounded-full bg-white shadow transition-all ${a.enabled ? "left-7" : "left-1"}`}></span>
          </button>
        </div>

        <div className={`${cardCls} space-y-4`}>
          <h3 className="font-heading font-bold text-foreground-950">Personalidad y reglas</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Nombre del agente">
              <input value={a.agentName} onChange={(e) => set("agentName", e.target.value)} maxLength={40} className={inputCls} />
            </Field>
            <Field label="Tono">
              <input value={a.tone} onChange={(e) => set("tone", e.target.value)} maxLength={200} className={inputCls} />
            </Field>
          </div>
          <Field label="Objetivo" hint="Para qué existe este agente.">
            <textarea rows={2} value={a.goal} onChange={(e) => set("goal", e.target.value)} className={`${inputCls} resize-y`} maxLength={600} />
          </Field>
          <Field label="Contexto del negocio" hint="Quiénes son, qué ofrecen, a quién ayudan, qué NO hacen.">
            <textarea rows={4} value={a.businessContext} onChange={(e) => set("businessContext", e.target.value)} className={`${inputCls} resize-y`} maxLength={3000} />
          </Field>
          <Field label="Instrucciones de conversación" hint="Cómo debe llevar la charla: qué preguntar primero, cuándo compartir el enlace, cómo cerrar…">
            <textarea rows={4} value={a.instructions} onChange={(e) => set("instructions", e.target.value)} className={`${inputCls} resize-y`} maxLength={3000} />
          </Field>
          <Field label="Prohibido" hint="Además de los términos prohibidos del panel de Cumplimiento, que ya se aplican siempre.">
            <textarea rows={3} value={a.forbidden} onChange={(e) => set("forbidden", e.target.value)} className={`${inputCls} resize-y`} maxLength={1500} />
          </Field>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Saludo inicial" hint="{nombre} y {agente} se reemplazan solos.">
              <textarea rows={2} value={a.greeting} onChange={(e) => set("greeting", e.target.value)} className={`${inputCls} resize-y`} maxLength={300} />
            </Field>
            <Field label="Mensaje al pasar a una persona">
              <textarea rows={2} value={a.fallback} onChange={(e) => set("fallback", e.target.value)} className={`${inputCls} resize-y`} maxLength={300} />
            </Field>
          </div>
          <Field label="Palabras que pasan a una persona" hint="Separadas por comas. Si el cliente las escribe, el agente se detiene.">
            <input value={a.handoffKeywords} onChange={(e) => set("handoffKeywords", e.target.value)} className={inputCls} maxLength={600} />
          </Field>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Máx. respuestas automáticas por conversación" hint="Al llegar al límite pasa a una persona.">
              <input type="number" min={1} max={30} value={a.maxAutoReplies} onChange={(e) => set("maxAutoReplies", Number(e.target.value))} className={inputCls} />
            </Field>
            <Field label="Espera antes de responder (segundos)" hint="Se siente más natural y permite que un humano intervenga.">
              <input type="number" min={0} max={60} value={a.replyDelaySeconds} onChange={(e) => set("replyDelaySeconds", Number(e.target.value))} className={inputCls} />
            </Field>
          </div>
          <div className="flex items-center gap-3">
            <button className={btnPrimary} disabled={!dirty || busy} onClick={() => void save()}>
              Guardar cambios
            </button>
            {dirty ? <span className="text-xs text-amber-700">Cambios sin guardar</span> : null}
          </div>
        </div>

        <div className={`${cardCls} space-y-3`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 className="font-heading font-bold text-foreground-950">Base de conocimiento</h3>
              <p className="text-[11px] text-foreground-500">Lo único de lo que el agente puede sacar datos concretos (precios, fechas, políticas, pasos). Si no está aquí, no lo inventa: pasa a una persona.</p>
            </div>
            <div className="flex gap-2">
              {!kb.length ? (
                <button
                  className={btnGhost}
                  onClick={async () => {
                    try {
                      for (const s of STARTER) await saveKnowledge({ ...s, active: true });
                      notify("Ejemplos cargados. Edítalos con tus datos reales.");
                      await load();
                    } catch {
                      notify("No se pudieron cargar");
                    }
                  }}
                >
                  Cargar ejemplos
                </button>
              ) : null}
              <button className={btnPrimary} onClick={() => setEditing({})}>
                + Añadir
              </button>
            </div>
          </div>
          {editing && !editing.id ? (
            <KnowledgeEditor
              item={editing}
              notify={notify}
              onDone={() => {
                setEditing(null);
                void load();
              }}
            />
          ) : null}
          {!kb.length && !editing ? <div className="text-sm text-foreground-500">Todavía no hay conocimiento. Añade preguntas frecuentes y sus respuestas.</div> : null}
          {kb.map((k) =>
            editing?.id === k.id ? (
              <KnowledgeEditor
                key={k.id}
                item={editing}
                notify={notify}
                onDone={() => {
                  setEditing(null);
                  void load();
                }}
              />
            ) : (
              <div key={k.id} className={`rounded-xl border p-3 ${k.active ? "border-background-200 bg-background-50" : "border-dashed border-background-300 opacity-60"}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-foreground-950">{k.question || k.title}</div>
                    <div className="text-xs text-foreground-600 mt-1 whitespace-pre-wrap">{k.answer}</div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button className="w-8 h-8 rounded-full hover:bg-background-200 cursor-pointer" aria-label="Editar" onClick={() => setEditing(k)}>
                      <i className="ri-edit-line"></i>
                    </button>
                    <button
                      className="w-8 h-8 rounded-full hover:bg-accent-500/10 text-accent-700 cursor-pointer"
                      aria-label="Eliminar"
                      onClick={async () => {
                        if (!window.confirm("¿Eliminar esta respuesta?")) return;
                        try {
                          await deleteKnowledge(k.id);
                          await load();
                        } catch {
                          notify("No se pudo eliminar");
                        }
                      }}
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </div>
                </div>
              </div>
            ),
          )}
        </div>
      </div>

      <div className="lg:sticky lg:top-4">
        <Tester notify={notify} />
      </div>
    </div>
  );
}
